import { useTranslation } from 'react-i18next';
import { AccordionDetails, Checkbox, FormControlLabel, Grid, MenuItem, Select, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { AccountData, OrderData, OrderPageData, OrderStatus } from '../../../../../api';
import { Accordion, AccordionSummary, AccordionSummaryVariant } from '../Accordion';
import { EditOrderFormValues, EditOrderPanels } from '../../EditOrderDialog';
import { SeatingPanelSummary } from './SeatingPanelSummary';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { ResourceSelectionPanel } from '../resourceSelectionPanel/ResourceSelectionPanel';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { localizedLabel } from '../../../../../utils/maskUtils';
import { RESOURCE_SELECTION_FIELD_ID } from '../../../../../common/OrderUtils';

type SeatingPanelProps = {
  isEventOrderPage: boolean;
  expanded?: EditOrderPanels;
  handleChange: any;
  orderId?: string;
  isOneOfVoucherIntents: boolean;
  orderOrVoucher: Partial<OrderData>;
  orderPageId?: string;
  persons?: number;
  orderPage?: OrderPageData;
};

/** Tables (scheduler resources) and the customer's area choice, kept apart from the management comment. */
export const SeatingPanel = ({
  isEventOrderPage,
  expanded,
  handleChange,
  orderId,
  isOneOfVoucherIntents,
  orderOrVoucher,
  orderPageId,
  persons,
  orderPage,
}: SeatingPanelProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const language = useLanguage();
  const account = authState.account as unknown as AccountData;
  const { values, initialValues, setFieldValue } = useFormikContext<EditOrderFormValues>();
  const schedulerConfigs = account.shared_resources?.scheduler_configs ?? [];
  const resourceOptions =
    schedulerConfigs
      .flatMap((schedulerConfig) => schedulerConfig.allowed_masks ?? [])
      .find((mask) => mask.order_page_id === orderPageId)?.resource_options ?? [];

  const editedDuration = values.privateOrderEndTimeSeconds - values.timeSeconds;
  const durationSeconds = editedDuration > 0 ? editedDuration : orderOrVoucher?.duration ?? orderPage?.duration;
  const isArchived = !!orderId && orderOrVoucher?.status === OrderStatus.ARCHIVED;

  // A moved order loses its tables (the server re-seats it), so the picker starts empty at the new slot.
  // Runs whether or not the panel is shown, so it lives above the early return.
  const isMovedOrder =
    !!orderId && (values.date !== initialValues.date || values.timeSeconds !== initialValues.timeSeconds);
  useEffect(() => {
    if (isMovedOrder) {
      void setFieldValue('selectedHolds', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMovedOrder]);

  const scheduledIndexes = isMovedOrder || values.skipResourceAssignment ? [] : orderOrVoucher?.resource_indexes ?? [];
  const showTables = schedulerConfigs.length > 0 && !!orderPageId && !!persons;
  const showResourceOptions = resourceOptions.length > 0;

  if (isEventOrderPage || isOneOfVoucherIntents || (!showTables && !showResourceOptions)) {
    return null;
  }

  return (
    <Accordion
      onChange={handleChange}
      expandedPanelName={expanded}
      panelName={EditOrderPanels.SeatingPanel}
      allOpenValue={EditOrderPanels.AllOpen}>
      <AccordionSummary
        variant={AccordionSummaryVariant.CAPTION}
        title={t('calendar.edit_order.seatingSection')}
        summaryComponent={
          <SeatingPanelSummary
            showTables={showTables}
            selectedHolds={values.selectedHolds}
            scheduledIndexes={scheduledIndexes}
            orderPageId={orderPageId}
            skipResourceAssignment={values.skipResourceAssignment}
            resourceOptionLabel={
              values.resourceOptionId
                ? localizedLabel(
                    resourceOptions.find((option) => option.id === values.resourceOptionId)?.label,
                    language,
                    values.resourceOptionId,
                  )
                : undefined
            }
          />
        }
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={2}>
          {showTables && (
            <>
              <Grid id={RESOURCE_SELECTION_FIELD_ID}>
                <ResourceSelectionPanel
                  orderPageId={orderPageId}
                  persons={persons}
                  selectedHolds={values.selectedHolds}
                  setSelectedHolds={(holds) => setFieldValue('selectedHolds', holds)}
                  disabled={isArchived}
                  date={values.date}
                  startSeconds={values.timeSeconds}
                  durationSeconds={durationSeconds}
                  excludeOrderId={orderId}
                  scheduledIndexes={scheduledIndexes}
                />
              </Grid>
              {/* Seating a walk-in when the grid is full: the order keeps its slot but holds no
                  table, so it neither blocks capacity nor gets re-seated by the next booking's
                  reshuffle. Clears the table selection too, since holding tables and sitting off
                  the grid are contradictory. */}
              {orderId && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.skipResourceAssignment}
                      disabled={orderOrVoucher?.status === OrderStatus.ARCHIVED}
                      onChange={(event) => {
                        void setFieldValue('skipResourceAssignment', event.target.checked);
                        void setFieldValue('selectedHolds', event.target.checked ? [] : initialValues.selectedHolds);
                      }}
                    />
                  }
                  label={t('calendar.edit_order.skipResourceAssignment')}
                />
              )}
            </>
          )}

          {showResourceOptions && (
            <Grid container flexDirection="column" gap={0.5}>
              <Typography variant="subtitle2">{t('calendar.edit_order.resourceOption')}</Typography>
              <Select
                size="small"
                sx={{ maxWidth: 300 }}
                value={values.resourceOptionId}
                displayEmpty
                disabled={isArchived}
                renderValue={(selected) =>
                  selected
                    ? localizedLabel(
                        resourceOptions.find((option) => option.id === selected)?.label,
                        language,
                        selected,
                      )
                    : t('calendar.edit_order.resourceOptionNone')
                }
                onChange={(event) => setFieldValue('resourceOptionId', event.target.value)}>
                {resourceOptions.map((resourceOption) => (
                  <MenuItem key={resourceOption.id} value={resourceOption.id}>
                    {localizedLabel(resourceOption.label, language, resourceOption.id)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
